import { SectionConfig } from "./types";
import { SHARED_CURRENCIES } from "../shared/constants";
import { API_ENDPOINTS, SERVICES } from "../api-endpoints";

export const API_CONFIG = {
  PAGE_KEY: 'home-web',
  CURRENCY: 'AED',
} as const;

// Currency mapping by country code
const COUNTRY_CURRENCY_MAP: Record<string, string> = {
  'AE': 'AED',
  'US': 'USD',
  'GB': 'GBP',
  'EU': 'EUR',
  'CH': 'CHF',
  'SA': 'SAR',
};

export const getCurrencyByCountryCode = (countryCode: string): string => {
  if (!countryCode) return API_CONFIG.CURRENCY;
  return COUNTRY_CURRENCY_MAP[countryCode.toUpperCase()] || API_CONFIG.CURRENCY;
};

export const DEFAULT_VALUES = {
  CATEGORY: '',
  REVIEW_COUNT: 0,
  RATING: 0,
} as const;

export const CMS_STATUS_CONFIG: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-red-100 text-red-800',
  hidden: 'bg-gray-100 text-gray-800',
};

export const UI_TEXT = {
  PRICE_NOTE: 'Price total for 1 nights',
  CURRENCY_LABEL: `(${API_CONFIG.CURRENCY})`,

  // CMS Dashboard Text
  CMS_PAGE_TITLE: 'Content Management',
  CMS_PAGE_SUBTITLE: 'Manage your website content and sections',
  SECTION_VISIBLE: 'Section Visible',
  SECTION_HIDDEN: 'Section Hidden',
  SECTION_DESCRIPTION: 'This section is visible on the frontend',
  FEATURED_ON_HOMEPAGE: 'Featured on Homepage',

  // Confirmation Modal Text
  HIDE_FROM_FRONTEND: 'Hide from Frontend?',
  SHOW_ON_FRONTEND: 'Show on Frontend?',
  SECTION_WILL_BE_HIDDEN: 'The entire section will be hidden from your website.',
  SECTION_WILL_BE_SHOWN: 'The entire section will be shown on your website.',
  ITEM_WILL_BE_HIDDEN: 'This item will be hidden from your website.',
  ITEM_WILL_BE_SHOWN: 'This item will be shown on your website.',
  YES_HIDE: 'Yes, Hide',
  YES_SHOW: 'Yes, Show',
  // Modal Buttons & Labels
  CANCEL: 'Cancel',
  CLOSE: 'Close',
  SAVE_CHANGES: 'Save Changes',
  ADD_EXPERIENCE: 'Add New Experience',
  ADD_HOTEL: 'Add New Hotel',
  ADD_CITY: 'Add New City',
  ADD_RESORT: 'Add New Resort',
  ADD_COUNTRY: 'Add New Country',
  DONE: 'Done',

  // Validation & Tooltips
  ALREADY_ADDED: 'Already added to this section',
  DISPLAY_ON_FRONTEND: 'Display on Frontend',
  FINDING_HOTELS: 'Finding hotels...',
  NO_HOTELS_MATCHING: 'No hotels found matching your search',
  SEARCH_HOTELS_PLACEHOLDER: 'Search by name, ID, location, or brand...',
  AMENITIES_FOOTER_TEXT: 'Selected amenities will be displayed on the frontend for this hotel',
  NO_IMAGE_TEXT: 'No Image',
  PRIVACY_PROTECTION_TITLE: 'Privacy Protection',

  // Manage Hotels
  MANAGE_HOTELS: 'Manage Hotels',
  AVAILABLE_HOTELS: 'Available Hotels',
  SELECTED_HOTELS: 'Selected Hotels',
  NO_HOTELS_SELECTED: 'No hotels selected yet',
  ADD: 'Add',

  // Error Messages
  GENERAL_ERROR: 'An error occurred while saving.',
  VALIDATION_ERROR: 'Please fix the validation errors',
  VALIDATION_SUMMARY_TITLE: 'Please fix the following errors:',
  ERROR_LOADING_HOTELS: 'Error loading hotels:',
  HOTEL: 'Hotel',
  HOTELS: 'Hotels',
  SELECTED_FOR: 'selected for',
  LOCATION_PLACEHOLDER: 'Location',
  LOADING_SECTIONS: 'Loading sections...',
  SECTION_STATUS_UPDATED: 'Section status updated',
  ITEM_STATUS_UPDATED: 'Item status updated',
  ERROR_UPDATING_STATUS: 'Error updating status',
} as const;

export const MODAL_IDENTIFIER_FIELDS = ['cityName', 'countryName', 'experienceName', 'name'];

export const SECTION_KEYS = {
  EXCLUSIVE_OFFERS: 'exclusive-offers',
  WORLD_FINEST_DESTINATIONS: 'world-finest-destinations',
  PREFERRED_DESTINATIONS: 'preferred-destinations',
  PRIVATE_ISLAND_RESORTS: 'private-island-resorts',
  BESPOKE_EXPERIENCES: 'bespoke-experiences',
  MARKUP_MANAGEMENT: 'markup-management',
} as const;

export const SECTION_KEY_MAP: Record<string, string> = {
  [SECTION_KEYS.EXCLUSIVE_OFFERS]: 'exclusiveOffers',
  [SECTION_KEYS.WORLD_FINEST_DESTINATIONS]: 'worldFinestDestinations',
  [SECTION_KEYS.PREFERRED_DESTINATIONS]: 'preferredDestinations',
  [SECTION_KEYS.PRIVATE_ISLAND_RESORTS]: 'privateIslandResorts',
  [SECTION_KEYS.BESPOKE_EXPERIENCES]: 'bespokeExperiences',
  [SECTION_KEYS.MARKUP_MANAGEMENT]: 'markupManagement',
} as const;

export const INITIAL_SECTION_VISIBILITY: Record<string, boolean> = {
  [SECTION_KEYS.EXCLUSIVE_OFFERS]: true,
  [SECTION_KEYS.WORLD_FINEST_DESTINATIONS]: true,
  [SECTION_KEYS.PREFERRED_DESTINATIONS]: true,
  [SECTION_KEYS.PRIVATE_ISLAND_RESORTS]: true,
  [SECTION_KEYS.BESPOKE_EXPERIENCES]: true,
} as const;

// Section matching keywords for API response matching
export const SECTION_TITLE_KEYWORDS: Record<string, string[]> = {
  [SECTION_KEYS.EXCLUSIVE_OFFERS]: ['exclusive'],
  [SECTION_KEYS.WORLD_FINEST_DESTINATIONS]: ['finest'],
  [SECTION_KEYS.PREFERRED_DESTINATIONS]: ['preferred'],
  [SECTION_KEYS.PRIVATE_ISLAND_RESORTS]: ['private', 'island'],
  [SECTION_KEYS.BESPOKE_EXPERIENCES]: ['bespoke', 'experience'],
} as const;

export const SECTION_CONFIGS: SectionConfig[] = [
  {
    id: SECTION_KEYS.EXCLUSIVE_OFFERS,
    label: 'Exclusive Offers',
    sectionKey: SECTION_KEYS.EXCLUSIVE_OFFERS,
    gridColumns: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3',
    dialogType: 'hotel',
    buttonLabel: 'Add New Hotel',
    needsHotelDialog: true,
    maxFeatured: 6,
  },
  {
    id: SECTION_KEYS.WORLD_FINEST_DESTINATIONS,
    label: "World's Finest Destinations",
    sectionKey: SECTION_KEYS.WORLD_FINEST_DESTINATIONS,
    gridColumns: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4',
    dialogType: 'city',
    buttonLabel: 'Add New City',
    needsHotelDialog: false,
    maxFeatured: 12,
    hasAddCountry: true,
  },
  {
    id: SECTION_KEYS.PRIVATE_ISLAND_RESORTS,
    label: 'Private Island Resorts',
    sectionKey: SECTION_KEYS.PRIVATE_ISLAND_RESORTS,
    gridColumns: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3',
    dialogType: 'hotel',
    buttonLabel: 'Add New Resort',
    needsHotelDialog: true,
    maxFeatured: 6,
  },
  {
    id: SECTION_KEYS.PREFERRED_DESTINATIONS,
    label: 'Preferred Destinations',
    sectionKey: SECTION_KEYS.PREFERRED_DESTINATIONS,
    gridColumns: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3',
    dialogType: 'hotel',
    buttonLabel: 'Add New Hotels',
    needsHotelDialog: true,
    maxFeatured: 6,
  },
  {
    id: SECTION_KEYS.BESPOKE_EXPERIENCES,
    label: 'Bespoke Experiences',
    sectionKey: SECTION_KEYS.BESPOKE_EXPERIENCES,
    gridColumns: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3',
    dialogType: 'hotel',
    buttonLabel: 'Add New Experience',
    needsHotelDialog: true,
    maxFeatured: 6,
    hasManageHotels: true,
  },
] as const;

export function getApiUrl(endpoint: string, params: Record<string, string> = {}): string {
  let url = `${SERVICES.ADMIN}${endpoint}`;
  Object.entries(params).forEach(([key, value]) => {
    url = url.replace(`{${key}}`, value);
  });
  return url;
}

export function getPagesUrl(): string {
  return API_ENDPOINTS.CMS.PAGES();
}

export function getSectionsUrl(): string {
  return API_ENDPOINTS.CMS.SECTIONS(API_CONFIG.PAGE_KEY);
}

export function getCreateItemUrl(pageId: string, sectionId: string): string {
  return API_ENDPOINTS.CMS.SECTION_ITEMS(pageId, sectionId);
}

export function getUpdateItemUrl(pageId: string, sectionId: string, itemId: string): string {
  return API_ENDPOINTS.CMS.SECTION_ITEM_DETAILS(pageId, sectionId, itemId);
}

export function getDeleteItemUrl(pageId: string, sectionId: string, itemId: string): string {
  return API_ENDPOINTS.CMS.SECTION_ITEM_DETAILS(pageId, sectionId, itemId);
}

export function getHotelsUrl(): string {
  return API_ENDPOINTS.CMS.HOTELS();
}

export function getImageUrl(imagePath: string): string {
  return API_ENDPOINTS.CMS.IMAGE(imagePath);
}

export function extractImagePathFromUrl(fullUrl: string): string {
  if (!fullUrl) return '';
  const baseUrl = SERVICES.ADMIN.replace(/^https?:\/\//, '').replace(/^\/+/, '');
  if (fullUrl.includes(baseUrl)) {
    return fullUrl.split(baseUrl + '/')[1] || '';
  }
  return fullUrl;
}

// Helper function to match section by key or title keywords
export function matchSectionByKeyOrTitle(
  sectionKey: string,
  apiKey: string,
  apiTitle: string
): boolean {
  const backendKey = SECTION_KEY_MAP[sectionKey] || sectionKey;

  // Exact key match
  if (apiKey === backendKey || apiKey === sectionKey) return true;

  // Fallback to title matching using keywords
  const keywords = SECTION_TITLE_KEYWORDS[sectionKey] || [];
  const titleLower = apiTitle.toLowerCase();

  return keywords.some(keyword => titleLower.includes(keyword));
}